import React, { useEffect, useState } from 'react';
import { useAuth } from '../../auth/firebaseAuth';
import { getDocumentById } from '../../utility/firebaseHelpers';
import { signOut } from 'firebase/auth';
import { auth } from '../../auth/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface BanInfo {
  bannedAt?: any;
  bannedReason?: string;
  bannedBy?: string;
}

const BannedAccountPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [banInfo, setBanInfo] = useState<BanInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBanInfo = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDocumentById('users', currentUser.uid);

        if (userDoc && userDoc.status === 'banned') {
          setBanInfo({
            bannedAt: userDoc.bannedAt,
            bannedReason:
              userDoc.bannedReason || 'Violation of terms of service',
            bannedBy: userDoc.bannedBy
          });
        } else {
          // If user is not banned, redirect to home
          navigate('/', { replace: true });
        }
      } catch (error) {
        console.error('Error fetching ban info:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanInfo();
  }, [currentUser, navigate]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10 border-t-4 border-red-600">
          <div className="flex justify-center mb-6">
            <div className="rounded-full bg-red-100 p-4">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-600" />
            </div>
          </div>

          <h2 className="text-center text-3xl font-extrabold text-gray-900 mb-4">
            Account Suspended
          </h2>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-800 text-center">
              Your RentSmart account has been suspended due to violations of our
              Terms of Service.
            </p>
          </div>

          {banInfo && (
            <div className="space-y-4 mb-6">
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Reason:
                </h3>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                  {banInfo.bannedReason}
                </p>
              </div>

              {banInfo.bannedAt && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Suspended On:
                  </h3>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                    {new Date(banInfo.bannedAt.toDate()).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">
              What can you do?
            </h3>
            <ul className="list-disc list-inside text-sm text-blue-800 space-y-1">
              <li>Review our Terms of Service</li>
              <li>Contact our support team if you believe this is a mistake</li>
              <li>Appeal this decision through our contact form</li>
            </ul>
          </div>

          <div className="space-y-3">
            <a
              href="/contact"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Contact Support
            </a>

            <button
              onClick={handleSignOut}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Sign Out
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              For more information, please read our{' '}
              <a
                href="/terms-of-service"
                className="text-blue-600 hover:text-blue-500"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href="/privacy-policy"
                className="text-blue-600 hover:text-blue-500"
              >
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannedAccountPage;
